import type { PopoverContentProps } from '@radix-ui/react-popover';
import {
  type FocusEvent,
  type KeyboardEvent,
  type MouseEvent,
  type RefObject,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';
import { getNextTabbable, getTabbables } from '../utils/getTabbables';

const isTabKey = (e: KeyboardEvent) =>
  e.key === 'Tab' && !e.metaKey && !e.ctrlKey && !e.altKey && !e.nativeEvent.isComposing;

const clearTimer = (timerRef: RefObject<ReturnType<typeof setTimeout> | null>) => {
  if (timerRef.current != null) {
    clearTimeout(timerRef.current);
    timerRef.current = null;
  }
};

// Only one card popover is open at a time: opening one closes the previous card's.
let closeOpenCardPopover: (() => void) | null = null;

export type UseCardPopoverOptions = {
  /** Visible name of the card; the popover is labelled "<name> details". */
  name: string;
  /** Passed to `onHover` while the pointer or keyboard focus is on the card. */
  hoverId?: string;
  onHover?: (id?: string) => void;
  onFocus?: (e: FocusEvent<HTMLDivElement>) => void;
  /**
   * When provided: hover and focus open the popover, click closes it and calls this.
   * When omitted: click toggles the popover through the Radix trigger.
   */
  onFlyToClick?: (e: MouseEvent | KeyboardEvent) => void;
};

/** Open/close, hover and keyboard focus handling shared by the map-list cards (ProjectCard, RequestCard). */
export function useCardPopover({ name, hoverId, onHover, onFocus, onFlyToClick }: UseCardPopoverOptions) {
  const [isOpen, setIsOpen] = useState(false);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Keyboard focus only: a mouse click also focuses the card, and hover should still end on mouse leave.
  const keyboardFocusRef = useRef(false);
  const rowRef = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  // Set by Escape while the card has keyboard focus, so refocusing it doesn't reopen the popover.
  const dismissedRef = useRef(false);
  // Set when a close should hand focus back to the card; Radix only restores focus to a trigger, and anchor mode has none.
  const returnFocusRef = useRef(false);
  const contentId = useId();

  useEffect(() => {
    return () => clearTimer(closeTimeoutRef);
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    const close = () => {
      // A keyboard user's popover stays open while the pointer passes over other cards, as on mouse leave.
      if (keyboardFocusRef.current || contentKeyboardFocusRef.current) return;
      clearTimer(closeTimeoutRef);
      setIsOpen(false);
    };
    closeOpenCardPopover?.();
    closeOpenCardPopover = close;
    return () => {
      if (closeOpenCardPopover === close) closeOpenCardPopover = null;
    };
  }, [isOpen]);

  const clearCloseTimeout = () => clearTimer(closeTimeoutRef);

  const cardHasKeyboardFocus = () => keyboardFocusRef.current;

  // Set when keyboard focus enters the popover; a mouse press on one of its buttons leaves it false.
  const contentKeyboardFocusRef = useRef(false);
  const popoverHasKeyboardFocus = () => contentKeyboardFocusRef.current;

  const handleRowMouseEnter = () => {
    clearCloseTimeout();
    if (onFlyToClick && !dismissedRef.current) setIsOpen(true);
  };

  const handleRowMouseLeave = () => {
    if (!onFlyToClick || cardHasKeyboardFocus() || popoverHasKeyboardFocus()) return;
    clearCloseTimeout();
    closeTimeoutRef.current = setTimeout(() => setIsOpen(false), 200);
  };

  const handleClick = (e: MouseEvent) => {
    // A click hands the card to the pointer, so keyboard-only state no longer applies.
    keyboardFocusRef.current = false;
    dismissedRef.current = false;
    if (onFlyToClick) {
      e.preventDefault();
      e.stopPropagation();
      setIsOpen(false);
      onFlyToClick(e);
    }
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (onFlyToClick) {
        setIsOpen(false);
        onFlyToClick(e);
      }
      return;
    }
    // The popover is portalled out of DOM order, so Tab has to be walked into it by hand.
    if (isTabKey(e) && !e.shiftKey && isOpen && contentRef.current) {
      const first = getTabbables(contentRef.current)[0];
      if (first) {
        e.preventDefault();
        contentKeyboardFocusRef.current = true;
        first.focus();
      }
    }
  };

  // Radix loops Tab inside the popover, so both edges hand focus back out: Shift+Tab to the card, Tab past it.
  const handleContentKeyDown = (e: KeyboardEvent) => {
    if (!isTabKey(e) || !contentRef.current || !cardRef.current) return;
    const tabbables = getTabbables(contentRef.current);
    const active = document.activeElement;
    if (e.shiftKey && active === tabbables[0]) {
      e.preventDefault();
      cardRef.current.focus();
    } else if (!e.shiftKey && active === tabbables[tabbables.length - 1]) {
      e.preventDefault();
      const next = getNextTabbable(cardRef.current, contentRef.current);
      if (next) {
        next.focus();
        return;
      }
      // Nothing follows the card: close the popover on the card so the next Tab leaves the page.
      dismissedRef.current = true;
      setIsOpen(false);
      cardRef.current.focus();
    }
  };

  const handleContentFocus = (e: FocusEvent<HTMLDivElement>) => {
    if ((e.target as HTMLElement).matches(':focus-visible')) contentKeyboardFocusRef.current = true;
  };

  const handleContentBlur = (e: FocusEvent<HTMLDivElement>) => {
    const next = e.relatedTarget as Node | null;
    if (contentRef.current?.contains(next)) return;
    contentKeyboardFocusRef.current = false;
    if (rowRef.current?.contains(next)) return;
    dismissedRef.current = false;
    onHover?.();
    setIsOpen(false);
  };

  // Focus mirrors hover: open the details popover and highlight the item on the map.
  const handleFocus = (e: FocusEvent<HTMLDivElement>) => {
    keyboardFocusRef.current = e.currentTarget.matches(':focus-visible');
    onHover?.(hoverId);
    if (onFlyToClick && !dismissedRef.current) {
      clearCloseTimeout();
      setIsOpen(true);
    }
    onFocus?.(e);
  };

  const handleBlur = (e: FocusEvent<HTMLDivElement>) => {
    keyboardFocusRef.current = false;
    // Focus moving into the popover (a mouse press on one of its buttons) keeps it open.
    if (contentRef.current?.contains(e.relatedTarget as Node | null)) return;
    dismissedRef.current = false;
    onHover?.();
    if (onFlyToClick) setIsOpen(false);
  };

  const rowProps = {
    ref: rowRef as RefObject<HTMLDivElement>,
    onMouseEnter: handleRowMouseEnter,
    onMouseLeave: handleRowMouseLeave,
  };

  const cardProps = {
    ref: cardRef as RefObject<HTMLDivElement>,
    'aria-haspopup': 'dialog' as const,
    'aria-expanded': isOpen,
    'aria-controls': isOpen ? contentId : undefined,
    onClick: handleClick,
    onKeyDown: handleKeyDown,
    onFocus: handleFocus,
    onBlur: handleBlur,
    onMouseEnter: () => onHover?.(hoverId),
    onMouseLeave: () => {
      if (!cardHasKeyboardFocus()) onHover?.();
    },
  };

  const contentProps = {
    ref: contentRef as RefObject<HTMLDivElement>,
    id: contentId,
    'aria-label': `${name} details`,
    onOpenAutoFocus: (e: Event) => e.preventDefault(),
    // Focus on this card isn't outside the popover; it anchors it.
    onFocusOutside: (e: Parameters<NonNullable<PopoverContentProps['onFocusOutside']>>[0]) => {
      if (rowRef.current?.contains(e.target as Node | null)) e.preventDefault();
    },
    onEscapeKeyDown: () => {
      if (cardHasKeyboardFocus()) dismissedRef.current = true;
      if (contentRef.current?.contains(document.activeElement)) {
        dismissedRef.current = true;
        returnFocusRef.current = true;
      }
    },
    onCloseAutoFocus: (e: Event) => {
      contentKeyboardFocusRef.current = false;
      if (!returnFocusRef.current) return;
      returnFocusRef.current = false;
      e.preventDefault();
      cardRef.current?.focus();
    },
    onKeyDown: handleContentKeyDown,
    onFocus: handleContentFocus,
    onBlur: handleContentBlur,
    onMouseEnter: onFlyToClick ? clearCloseTimeout : undefined,
    onMouseLeave: onFlyToClick ? handleRowMouseLeave : undefined,
  };

  return { isOpen, onOpenChange: setIsOpen, rowProps, cardProps, contentProps };
}
